import { supabase } from './supabase/client';
import type { Database, Tables } from 'src/types/db.types';

type FinancialScenario = Tables<'financial_scenarios'>;
type StudentFinancialChallenge = Tables<'student_financial_challenges'>;

export interface CreateScenarioDTO {
  title: string;
  description: string;
  amount: number;
}

export interface UpdateScenarioDTO extends Partial<CreateScenarioDTO> {}

export interface AssignChallengeDTO {
  studentId: string;
  scenarioId: string;
  amount: string;
  duration: string;
}

export interface ScenarioFilters {
  search?: string;
  page?: number;
  limit?: number;
}

export interface ScenarioListResponse {
  data: FinancialScenario[];
  total: number;
  page: number;
  limit: number;
}

export interface ChallengeFilters {
  studentId?: string;
  teacherId?: string;
  teacherUserId?: string;
  status?: 'In Progress' | 'Completed' | 'Overdue';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ChallengeListResponse {
  data: (StudentFinancialChallenge & {
    scenario: FinancialScenario;
    student: {
      user: {
        firstname: string;
        lastname: string;
        email: string;
      };
    };
  })[];
  total: number;
  page: number;
  limit: number;
}

export interface ScenarioOption {
  id: string;
  title: string;
  amount: number;
}

export interface WhammiesMetrics {
  totalChallenges: number;
  inProgressCount: number;
  completedCount: number;
  overdueCount: number;
  totalVolume: number;
  totalScenarios: number;
}

import { resolveAssignedStudentIdsForTeacher } from './student.service';

const SCENARIOS_TABLE = 'financial_scenarios';
const CHALLENGES_TABLE = 'student_financial_challenges';

function applyChallengeFilters(
  query: any,
  filters: { assignedStudentIds: string[] | null; studentId?: string; status?: string; search?: string }
) {
  let q = query;
  if (filters.assignedStudentIds !== null) {
    q = q.in('studentid', filters.assignedStudentIds);
  }
  if (filters.studentId) {
    q = q.eq('studentid', filters.studentId);
  }
  if (filters.status) {
    q = q.eq('status', filters.status);
  }
  if (filters.search && filters.search.trim()) {
    const term = filters.search.trim();
    q = q.or(
      `student.user.firstname.ilike.%${term}%,student.user.lastname.ilike.%${term}%,scenario.title.ilike.%${term}%`
    );
  }
  return q;
}

/**
 * Service for managing financial scenarios and student challenges
 */
export const financialSimulationService = {
  /**
   * Get all active scenarios with minimal data for dropdowns
   */
  async getAllScenarioOptions(): Promise<ScenarioOption[]> {
    try {
      const { data, error } = await supabase
        .from(SCENARIOS_TABLE)
        .select('id, title, amount')
        .is('deletedat', null)
        .order('title');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.getAllScenarioOptions]:', error);
      throw error;
    }
  },

  /**
   * Get scenarios with filters and pagination
   */
  async getScenarios({ search, page = 1, limit = 10 }: ScenarioFilters = {}): Promise<ScenarioListResponse> {
    try {
      let query = supabase.from(SCENARIOS_TABLE).select('*', { count: 'exact' });

      // Only get non-deleted scenarios
      query = query.is('deletedat', null);

      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdat', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: data as FinancialScenario[],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[FinancialSimulationService.getScenarios]:', error);
      throw error;
    }
  },

  /**
   * Get a single scenario by ID
   */
  async getScenarioById(id: string) {
    try {
      const { data, error } = await supabase
        .from(SCENARIOS_TABLE)
        .select(
          `*,
          student_financial_challenges (
            *,
            student: students (
              *,
              user: users (
                firstname,
                lastname,
                email
              )
            )
          )
          `
        )
        .eq('id', id)
        .is('deletedat', null)
        .single();

      if (error) throw error;
      if (data && data.student_financial_challenges) {
        data.student_financial_challenges = data.student_financial_challenges.filter(
          (c: any) => !c.deletedat && c.student && c.student.user && !c.student.user.deletedat
        );
      }
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.getScenarioById]:', error);
      throw error;
    }
  },

  /**
   * Create a new scenario
   */
  async createScenario(scenario: CreateScenarioDTO): Promise<FinancialScenario> {
    try {
      const { data, error } = await supabase.from(SCENARIOS_TABLE).insert([scenario]).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.createScenario]:', error);
      throw error;
    }
  },

  /**
   * Update an existing scenario
   */
  async updateScenario(id: string, updates: UpdateScenarioDTO): Promise<FinancialScenario> {
    try {
      const { data, error } = await supabase.from(SCENARIOS_TABLE).update(updates).eq('id', id).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.updateScenario]:', error);
      throw error;
    }
  },

  /**
   * Soft delete a scenario
   */
  async deleteScenario(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(SCENARIOS_TABLE)
        .update({ deletedat: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[FinancialSimulationService.deleteScenario]:', error);
      throw error;
    }
  },

  /**
   * Get student challenges with filters and pagination
   */
  async getChallenges({
    studentId,
    teacherId,
    teacherUserId,
    status,
    search,
    page = 1,
    limit = 10,
  }: ChallengeFilters = {}): Promise<ChallengeListResponse> {
    try {
      const assignedStudentIds = await resolveAssignedStudentIdsForTeacher({ teacherId, teacherUserId });
      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        return { data: [], total: 0, page, limit };
      }

      let query = supabase
        .from(CHALLENGES_TABLE)
        .select(
          `
            *,
            scenario:financial_scenarios(*),
            student:students!inner(
              *,
              user:users!inner(*)
            )
          `,
          {
            count: 'exact',
          }
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null);

      query = applyChallengeFilters(query, { assignedStudentIds, studentId, status, search });

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdat', { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: (data || []) as any,
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[FinancialSimulationService.getChallenges]:', error);
      throw error;
    }
  },

  /**
   * Get high-level KPI metrics for Whammies dashboard
   */
  async getMetrics(filters?: { teacherId?: string; teacherUserId?: string }): Promise<WhammiesMetrics> {
    try {
      const assignedStudentIds = filters ? await resolveAssignedStudentIdsForTeacher(filters) : null;
      if (assignedStudentIds !== null && assignedStudentIds.length === 0) {
        const { count: scenariosCount } = await supabase
          .from(SCENARIOS_TABLE)
          .select('id', { count: 'exact', head: true })
          .is('deletedat', null);

        return {
          totalChallenges: 0,
          inProgressCount: 0,
          completedCount: 0,
          overdueCount: 0,
          totalVolume: 0,
          totalScenarios: scenariosCount || 0,
        };
      }

      let challengesQuery = supabase
        .from(CHALLENGES_TABLE)
        .select(
          `
            id,
            status,
            amount,
            student:students!inner(
              user:users!inner(deletedat)
            )
          `
        )
        .is('deletedat', null)
        .is('student.user.deletedat', null);

      if (assignedStudentIds !== null) {
        challengesQuery = challengesQuery.in('studentid', assignedStudentIds);
      }

      const [{ data: challenges }, { count: scenariosCount }] = await Promise.all([
        challengesQuery,
        supabase.from(SCENARIOS_TABLE).select('id', { count: 'exact', head: true }).is('deletedat', null),
      ]);

      const list = challenges || [];
      const totalChallenges = list.length;
      const inProgressCount = list.filter((c) => c.status === 'In Progress').length;
      const completedCount = list.filter((c) => c.status === 'Completed').length;
      const overdueCount = list.filter((c) => c.status === 'Overdue').length;
      const totalVolume = list.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
      const totalScenarios = scenariosCount || 0;

      return {
        totalChallenges,
        inProgressCount,
        completedCount,
        overdueCount,
        totalVolume,
        totalScenarios,
      };
    } catch (error) {
      console.error('[FinancialSimulationService.getMetrics]:', error);
      return {
        totalChallenges: 0,
        inProgressCount: 0,
        completedCount: 0,
        overdueCount: 0,
        totalVolume: 0,
        totalScenarios: 0,
      };
    }
  },

  /**
   * Get a single challenge by ID with related data
   */
  async getChallengeById(id: string) {
    try {
      const { data, error } = await supabase
        .from(CHALLENGES_TABLE)
        .select(
          `
        *,
        scenario: financial_scenarios (*),
        student: students (
          *,
          user: users (
            firstname,
            lastname,
            email
          )
        ),
        loan: loans (*)
      `
        )
        .eq('id', id)
        .is('deletedat', null)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.getChallengeById]:', error);
      throw error;
    }
  },

  /**
   * Assign a scenario to a student
   */
  async assignChallenge(challenge: AssignChallengeDTO): Promise<StudentFinancialChallenge> {
    try {
      const { data, error } = await supabase
        .from(CHALLENGES_TABLE)
        .insert([
          {
            studentid: challenge.studentId,
            scenarioid: challenge.scenarioId,
            duration: challenge.duration,
            amount: challenge.amount,
          },
        ])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.assignChallenge]:', error);
      throw error;
    }
  },

  /**
   * Update challenge status
   */
  async updateChallengeStatus(
    id: string,
    status: 'In Progress' | 'Completed' | 'Overdue'
  ): Promise<StudentFinancialChallenge> {
    try {
      const { data, error } = await supabase.from(CHALLENGES_TABLE).update({ status }).eq('id', id).select().single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[FinancialSimulationService.updateChallengeStatus]:', error);
      throw error;
    }
  },
};
